/**
 * @group Server
 * 
 * @summary
 * <blockquote>Factory function to get the appropriate RequestContextManager implementation</blockquote>
 * 
 * @remarks
 * This factory function returns the correct RequestContextManager implementation based on
 * the current JavaScript environment:
 * 
 * - **Server (Node.js):** Returns RequestContextManager with AsyncLocalStorage support
 * - **Browser:** Returns browser stub (no-op)
 * - **React Native:** Returns native stub (no-op)
 * 
 * Uses conditional imports to handle platform-specific resolution:
 * - Server version is dynamically required only in Node.js runtime (not Edge)
 * - Browser and native versions use static imports (always safe)
 * - Factory checks NEXT_RUNTIME to prevent Turbopack from resolving server modules in Edge runtime
 * - Metro auto-resolves `.native.ts` for React Native
 * 
 * @returns RequestContextManager instance appropriate for the current environment
 * 
 * @example
 * ```typescript
 * import { getRequestContextManager } from '@shared/config/src/server/getRequestContextManager';
 * 
 * const requestContext = getRequestContextManager();
 * requestContext.initialize();
 * ```
 */

import { getJavascriptEnvironment, getNextjsRuntime, tryCatch } from '@shared/utils';
import type { Result, AppError } from '@shared/utils';
import type { RequestContextManagerInterface } from './RequestContextManagerInterface';
import { shouldLoadServerRequestContext } from './shouldLoadServerRequestContext';

// Static imports for browser and native (always safe)
import browserRequestContext from './RequestContextManager.browser';
import nativeRequestContext from './RequestContextManager.native';

// Server import is conditional to avoid Turbopack resolution issues in Edge runtime
// We'll use a lazy getter that only imports when needed (Node.js runtime only)
let serverRequestContext: RequestContextManagerInterface | null = null;

/**
 * Loads the server RequestContextManager module dynamically
 * 
 * @returns Result containing the loaded RequestContextManager or an error
 */
function loadServerRequestContextModule(): Result<RequestContextManagerInterface, AppError> {
  return tryCatch(() => {
    // Use eval with string concatenation to prevent Turbopack from statically analyzing
    // Split the module path to avoid static analysis
    const requireFunc = eval('require');
    const moduleBase = './Request';
    const moduleName = 'Context' + 'Manager';
    const modulePath = moduleBase + moduleName;
    
    // Require the module using relative path (same directory)
    // This avoids path resolution issues and works in both dev and build
    const RequestContextManagerModule = requireFunc(modulePath);
    const loadedContext: RequestContextManagerInterface = RequestContextManagerModule.default;
    
    return loadedContext;
  });
}

function getServerRequestContext(): RequestContextManagerInterface {
  // If already loaded, return it
  if (serverRequestContext) {
    return serverRequestContext;
  }

  // Check if we should load the server RequestContextManager
  if (!shouldLoadServerRequestContext()) {
    return browserRequestContext;
  }

  // Attempt to load the server RequestContextManager
  const loadResult = loadServerRequestContextModule();
  
  return loadResult.match({
    ok: (loadedContext) => {
      serverRequestContext = loadedContext;
      return loadedContext;
    },
    err: () => {
      // If require fails (e.g., in Edge runtime or build issues), fall back to browser stub
      // This prevents crashes during build or in unsupported environments
      return browserRequestContext;
    }
  });
}

/**
 * Get the appropriate RequestContextManager for the current environment
 * 
 * @returns RequestContextManager instance (server, browser stub, or native stub)
 */
/**
 * @internal
 */
export function getRequestContextManager(): RequestContextManagerInterface {
  // Check Next.js runtime first - Edge runtime should never use server RequestContextManager
  // This prevents Turbopack from trying to resolve server modules in Edge runtime
  const runtimeResult = getNextjsRuntime();
  
  const isEdgeRuntime = runtimeResult.match({
    ok: (runtime) => runtime === 'edge',
    err: () => false
  });
  
  if (isEdgeRuntime) {
    return browserRequestContext;
  }
  
  const envResult = getJavascriptEnvironment();
  
  return envResult.match({
    ok: (environment) => {
      switch (environment) {
        case 'node':
          // Call getServerRequestContext if we're in Node.js environment
          // getServerRequestContext will check NEXT_RUNTIME internally
          return getServerRequestContext();
        case 'browser':
          return browserRequestContext;
        case 'react-native':
          return nativeRequestContext;
        default:
          // Fallback to browser stub for unknown environments
          return browserRequestContext;
      }
    },
    err: () => {
      // Fallback to browser stub if environment detection fails
      // This prevents crashes in edge cases
      return browserRequestContext;
    }
  });
}

